import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useInView, useSpring } from "framer-motion";
import { ChevronDown, Scissors, Clock, Award, Star, ArrowRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import BookingDialog from "@/components/BookingDialog";
import { Link } from "react-router-dom";

const HERO_IMAGE = "/images/hero.jpg";
const LOGO_URL = "/images/logo.png";
const ABOUT_IMAGE = "/images/barberia.jpg";
const INTERIOR_IMAGE_2 = "/images/interior-2.jpg";

const services = [
  { name: "Taglio Deluxe", price: "37€", desc: "Taglio personalizzato con massaggio e tonico vitalizzante", age: "12+ anni", icon: Scissors },
  { name: "Taglio Tradizionale", price: "31€", desc: "Taglio classico con doppio shampoo e finish a scelta", age: "12+ anni", icon: Scissors },
  { name: "Barba Tradizionale", price: "27€", desc: "Sistemazione barba con prodotti di alta qualità", icon: Star },
  { name: "Barba Deluxe", price: "32€", desc: "Rasatura completa con vaporizzatore professionale", icon: Star },
  { name: "Barba Superior Xaikonix", price: "50€", desc: "Trattamento premium con lame Feather e cosmetici Xaikonix", icon: Award },
  { name: "Servizio Relax Kairos", price: "140€", desc: "90 minuti di puro relax con aperitivo e musica", icon: Clock },
];

const testimonials = [
  {
    name: "Marco R.",
    text: "Andrea è un vero artista! Ogni volta che vado da Kairos è un'esperienza unica. La cura nei dettagli, la professionalità e l'atmosfera rilassante fanno la differenza. Finalmente ho trovato il mio barbiere di fiducia.",
    rating: 5,
  },
  {
    name: "Vincenzo D.",
    text: "Il miglior Barbiere della zona! Servizio professionale e locale molto elegante, con parcheggio a due passi. Gentilissimi e disponibili per qualsiasi richiesta!",
    rating: 5,
  },
];

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      const numValue = parseFloat(value.replace(/[^0-9.]/g, ''));
      const duration = 2000;
      const steps = 60;
      const increment = numValue / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= numValue) {
          setDisplayValue(value);
          clearInterval(timer);
        } else {
          setDisplayValue(Math.floor(current).toString());
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return <span ref={ref}>{displayValue}{suffix}</span>;
};

// Magnetic Button Component
const MagneticButton = ({ children, onClick, className, testId }) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouse = (e) => {
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const x = (clientX - left - width / 2) * 0.2;
    const y = (clientY - top - height / 2) * 0.2;
    setPosition({ x, y });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <motion.button
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      animate={{ x: position.x, y: position.y }}
      transition={{ type: "spring", stiffness: 150, damping: 15 }}
      className={className}
      data-testid={testId}
    >
      {children}
    </motion.button>
  );
};

// Parallax Image Component
const ParallaxImage = ({ src, alt, className }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-[120%] object-cover"
      />
    </div>
  );
};

// Text Reveal Animation
const TextReveal = ({ children, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "100%" }}
        animate={isInView ? { y: 0 } : { y: "100%" }}
        transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export const HomePage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [cursorVariant, setCursorVariant] = useState("default");
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  // Smooth parallax for hero
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "50%"]);
  const heroOpacity = useTransform(heroProgress, [0, 0.5], [1, 0]);

  return (
    <div className="min-h-screen bg-kairos-black overflow-x-hidden" data-testid="home-page">
      {/* Progress Bar */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-[2px] bg-kairos-gold z-[60] origin-left"
        style={{ scaleX }}
      />
      
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image - No Parallax for clarity */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Andrea Dessì - Kairos La Barberia"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <motion.div 
          className="relative z-10 text-center px-6 max-w-4xl mx-auto"
          style={{ opacity: heroOpacity }}
        >
          <motion.img
            src={LOGO_URL}
            alt="Kairos"
            className="w-36 h-36 md:w-44 md:h-44 mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, ease: [0.33, 1, 0.68, 1] }}
          />
          
          <motion.p
            className="text-kairos-gold text-xs md:text-sm uppercase tracking-[0.4em] mb-6 font-accent"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Desenzano del Garda
          </motion.p>

          <div className="overflow-hidden mb-4">
            <motion.h1
              className="font-serif text-5xl sm:text-6xl md:text-8xl text-white tracking-tight"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.33, 1, 0.68, 1] }}
            >
              Il Momento Giusto
            </motion.h1>
          </div>
          
          <div className="overflow-hidden mb-8">
            <motion.h1
              className="font-serif text-5xl sm:text-6xl md:text-8xl text-kairos-gold tracking-tight"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.7, duration: 1, ease: [0.33, 1, 0.68, 1] }}
            >
              Per Te
            </motion.h1>
          </div>

          <motion.p
            className="text-white/70 text-lg md:text-xl max-w-xl mx-auto mb-12 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            Vivi un'esperienza unica e valorizza il tuo tempo
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
          >
            <MagneticButton
              onClick={() => setBookingOpen(true)}
              className="group relative bg-kairos-gold text-kairos-black px-12 py-5 text-sm uppercase tracking-[0.2em] font-bold overflow-hidden"
              testId="hero-book-btn"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                Prenota Ora
                <motion.span
                  className="inline-block"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <ArrowRight className="w-4 h-4" />
                </motion.span>
              </span>
              <motion.div
                className="absolute inset-0 bg-white"
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </MagneticButton>
            
            <Link
              to="/servizi"
              className="group border border-white/30 text-white px-12 py-5 text-sm uppercase tracking-[0.2em] font-medium hover:border-kairos-gold hover:text-kairos-gold transition-all duration-500 flex items-center justify-center gap-2"
              data-testid="hero-services-btn"
            >
              I Miei Servizi
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <span className="text-white/40 text-xs uppercase tracking-widest">Scorri</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ChevronDown className="w-6 h-6 text-kairos-gold" />
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section - REMOVED */}

      {/* Philosophy Section with Parallax */}
      <section className="py-32 px-6" data-testid="philosophy-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24 items-center">
            <div>
              <TextReveal>
                <p className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent">
                  Kairos: l'arte del tempo
                </p>
              </TextReveal>
              
              <TextReveal className="mb-6">
                <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight leading-tight">
                  In un mondo frenetico,
                </h2>
              </TextReveal>
              
              <TextReveal className="mb-8">
                <h2 className="font-serif text-4xl md:text-6xl text-kairos-gold tracking-tight">
                  prenditi cura di te
                </h2>
              </TextReveal>
              
              <motion.p 
                className="text-white/60 leading-relaxed mb-6 text-lg"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                Kairos la barberia valorizza il tempo dell'uomo, offrendo un'esperienza unica 
                e speciale in un ambiente accogliente e raffinato. Kairos rappresenta il momento 
                giusto per prendersi cura di sé e vivere un'esperienza di benessere autentica.
              </motion.p>
              
              <motion.blockquote
                className="border-l-2 border-kairos-gold pl-6 py-4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
              >
                <p className="text-white/80 italic text-lg mb-2">
                  "È donare qualità al tempo stesso, provando a goderne della sua essenza più 
                  preziosa e profonda."
                </p>
                <footer className="text-kairos-gold font-serif">— Andrea Dessì</footer>
              </motion.blockquote>
            </div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <ParallaxImage
                src={ABOUT_IMAGE}
                alt="Kairos Experience"
                className="relative aspect-[4/5]"
              />
              <motion.div 
                className="absolute -bottom-8 -left-8 bg-kairos-gold p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.05 }}
              >
                <p className="text-kairos-black font-serif text-3xl font-semibold">
                  Est. 2023
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 px-6 bg-kairos-gray/20" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <TextReveal>
              <p className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent">
                I Miei Servizi
              </p>
            </TextReveal>
            <TextReveal>
              <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight">
                Scopri l'eccellenza
              </h2>
            </TextReveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -8, borderColor: "rgba(197, 160, 89, 0.5)" }}
                className="bg-kairos-gray/30 border border-white/5 p-8 group cursor-pointer transition-colors duration-500"
                data-testid={`service-card-${index}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <motion.div
                      className="w-10 h-10 border border-kairos-gold/30 flex items-center justify-center"
                      whileHover={{ rotate: 360, borderColor: "#C5A059" }}
                      transition={{ duration: 0.5 }}
                    >
                      <service.icon className="w-5 h-5 text-kairos-gold" />
                    </motion.div>
                    <h3 className="font-serif text-xl text-white group-hover:text-kairos-gold transition-colors">
                      {service.name}
                    </h3>
                  </div>
                  <span className="text-kairos-gold text-2xl font-serif">
                    {service.price}
                  </span>
                </div>
                <p className="text-white/50 text-sm leading-relaxed mb-3">
                  {service.desc}
                </p>
                {service.age && (
                  <motion.span 
                    className="inline-block bg-kairos-gold/20 text-kairos-gold px-3 py-1 text-xs font-semibold border border-kairos-gold/30"
                    whileHover={{ scale: 1.05 }}
                  >
                    Età minima: {service.age}
                  </motion.span>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link
              to="/servizi"
              className="group inline-flex items-center gap-3 border border-kairos-gold/30 text-kairos-gold px-10 py-4 text-sm uppercase tracking-[0.2em] hover:border-kairos-gold hover:bg-kairos-gold/10 transition-all duration-300"
              data-testid="view-all-services-btn"
            >
              Tutti i Servizi
              <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Interior Showcase */}
      <section className="py-32 px-6" data-testid="interior-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              className="relative aspect-square overflow-hidden"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <ParallaxImage
                src={INTERIOR_IMAGE_2}
                alt="Interior"
                className="w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-kairos-black/60 to-transparent" />
            </motion.div>
            
            <div className="flex flex-col justify-center lg:pl-12">
              <TextReveal>
                <p className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent">
                  Il Mio Spazio
                </p>
              </TextReveal>
              <TextReveal className="mb-6">
                <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight">
                  Un ambiente pensato per te
                </h2>
              </TextReveal>
              <motion.p
                className="text-white/60 leading-relaxed mb-8"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                Ogni dettaglio del mio salone è stato curato per offrirti un'esperienza 
                di relax e benessere. Dall'arredamento elegante alle luci calde, tutto è 
                pensato per farti sentire a tuo agio.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <Link
                  to="/galleria"
                  className="group inline-flex items-center gap-2 text-kairos-gold hover:text-kairos-gold-light transition-colors"
                >
                  <span className="uppercase tracking-wider text-sm">Esplora la galleria</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 px-6 bg-kairos-gray/20" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <TextReveal>
              <p className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent">
                Recensioni
              </p>
            </TextReveal>
            <TextReveal>
              <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight">
                Cosa dicono di me
              </h2>
            </TextReveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.6 }}
                whileHover={{ x: 10 }}
                className="border-l-2 border-kairos-gold/30 pl-8 py-2 hover:border-kairos-gold transition-colors cursor-default"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.svg 
                      key={i} 
                      className="w-5 h-5 text-kairos-gold fill-current"
                      viewBox="0 0 20 20"
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </motion.svg>
                  ))}
                </div>
                <p className="text-white/70 leading-relaxed mb-6 italic text-lg">
                  "{testimonial.text}"
                </p>
                <p className="text-white font-medium">{testimonial.name}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <a
              href="https://www.google.com/search?q=KAIROS+LA+BARBERIA+Recensioni"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-kairos-gold hover:text-kairos-gold-light transition-colors text-sm uppercase tracking-wider"
              data-testid="all-reviews-link"
            >
              Leggi tutte le recensioni
              <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 relative overflow-hidden" data-testid="cta-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #C5A059 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <TextReveal>
              <p className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent">
                Prenota Ora
              </p>
            </TextReveal>
            <TextReveal className="mb-6">
              <h2 className="font-serif text-4xl md:text-6xl text-white tracking-tight">
                È il momento giusto per te
              </h2>
            </TextReveal>
            <motion.p
              className="text-white/60 max-w-xl mx-auto mb-12 text-lg"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              Scarica l'app e prenota il tuo appuntamento in pochi tap. 
              Vivi un'esperienza unica da Kairos La Barberia.
            </motion.p>
            
            <MagneticButton
              onClick={() => setBookingOpen(true)}
              className="group relative bg-kairos-gold text-kairos-black px-14 py-6 text-sm uppercase tracking-[0.2em] font-bold overflow-hidden"
              testId="cta-book-btn"
            >
              <span className="relative z-10 flex items-center gap-3">
                Prenota il tuo Appuntamento
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </span>
              <motion.div
                className="absolute inset-0 bg-white"
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </MagneticButton>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
};

export default HomePage;
